// app/users/page.tsx
"use client";
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/components/feature/AuthContext";
import { allAgents } from "@/mocks/leadsData";
import type { Branch, Status } from "@/mocks/authData";
import CrmLayout from "@/components/feature/CrmLayout";
import UserModal from "./components/UserModal";
import { useToast } from "@/components/base/Toast";
import axios from "axios";
import branchesApi, { apiError } from "../../../components/api/Branchesapi";

const API = import.meta.env.VITE_API_URL || "https://api.nexflowcrm.com/api/v1";
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
});


// ─── Types ───────────────────────────────────────────────────
interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  branch: string;
  branchId: string;
  status: "Active" | "Inactive";
  isActive: boolean;
  companyId: string;
  lastLogin: string;
  createdAt: string;
  password?: string;
  roleDetails?: {
    permissions: any[];
    additionalControls: Record<string, any>;
    description: string;
  };
  permissions?: any;
}


const statusColors: Record<string, string> = {
  Active: "bg-green-100 text-green-700",
  Inactive: "bg-gray-100 text-gray-500",
};

const positionColors: Record<string, string> = {
  super_admin: "bg-purple-100 text-purple-700",
  admin: "bg-blue-100 text-blue-700",
  sales_manager: "bg-indigo-100 text-indigo-700",
  sales_executive: "bg-cyan-100 text-cyan-700",
  viewer: "bg-gray-100 text-gray-600",
};

// ─── Component ───────────────────────────────────────────────
export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const { showToast } = useToast();

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [positionFilter, setPositionFilter] = useState("all");
  const [branchFilter, setBranchFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState<Status | "all">("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Deactivation flow
  const [deactivateUser, setDeactivateUser] = useState<User | null>(null);
  const [deactReassignLeadsTo, setDeactReassignLeadsTo] = useState("");
  const [deactUseAssignmentRules, setDeactUseAssignmentRules] = useState(false);
  const [deactReassignChatsTo, setDeactReassignChatsTo] = useState("");
  const [deactDistributeChatsEvenly, setDeactDistributeChatsEvenly] = useState(false);
  const [deactReassignCallsTo, setDeactReassignCallsTo] = useState("");
  const [deactNotifyTeam, setDeactNotifyTeam] = useState(true);
  const [positionsList, setPositionsList] = useState([]);
  const [branchList, setBranchList] = useState([]);

  // ─── Fetch all users ───────────────────────────────────────
  const fetchAllUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/auth/company-user/all`, {
        headers: authHeader(),
      });

      const items: any[] = res.data?.data?.items || [];
      console.log("AAAAAA===>", items)
      const mapped: User[] = items.map((u) => ({
        id: u.id,
        name: u.name || "",
        email: u.email || "",
        phone: u.phone || "",
        role: u.role || "user",
        branch: u.branch.name || "",
        branchId: u.branch.id || "",
        companyId: u.companyId || "",
        isActive: u.isActive ?? true,
        status: (u.isActive ?? true) ? "Active" : "Inactive",
        lastLogin: u.lastLogin || "Never",
        createdAt: u.createdAt || "",
        roleDetails: u.roleDetails || { permissions: [], additionalControls: {}, description: "" },
        permissions: u.permissions || null,
      }));
      setUsers(mapped);
    } catch (err: any) {
      showToast(err.response?.data?.message || "Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAllUsers(); }, [fetchAllUsers]);

  // ─── Helpers ─────────────────────────────────────────────────
  const getUserPosition = (role: string) =>
    positionsList.find((p) => p.name.toLowerCase().replace(/\s/g, "_") === role) || null;

  // ─── Filtered list ────────────────────────────────────────
  const filteredUsers = users.filter((u) => {
    if (search) {
      const q = search.toLowerCase();
      if (!u.name.toLowerCase().includes(q) && !u.email.toLowerCase().includes(q)) return false;
    }
    if (positionFilter !== "all" && getUserPosition(u.role)?.name !== positionFilter) return false;
    if (branchFilter !== "all" && u.branch !== branchFilter) return false;
    if (statusFilter !== "all" && u.status !== statusFilter) return false;
    return true;
  });

  const isSelf = (id: string) => currentUser?.id === id;

  // ─── Handlers ─────────────────────────────────────────────
  const handleAdd = () => { setEditingUser(null); setModalOpen(true); };
  const handleEdit = (u: User) => { setEditingUser(u); setModalOpen(true); };
  const handleDelete = (id: string) => setDeleteConfirm(id);

  // ─── Save (create + update) ───────────────────────────────
  const handleSave = async (
    data: Omit<User, "id" | "lastLogin">,
    id?: string
  ) => {
    setIsSaving(true);
    console.log("data===>", data)
    try {
      if (id) {
        // ✅ UPDATE — PATCH API
        await axios.patch(
          `${API}/auth/company-user/update/${id}`,
          {
            name: data.name,
            email: data.email,
            phone: data.phone,
            role: data.role,
            branch: data.branch,
            branchId: data.branchId,
            managerId: data.managerId,
            team: data.team,
            is_active: data.status === "Active",
            ...(data.password ? { password: data.password } : {}),
          },
          { headers: authHeader() }
        );
        showToast("User updated successfully", "success");
      } else {
        // ✅ CREATE
        const res = await axios.post(
          `${API}/auth/company-user/create`,
          {
            name: data.name,
            email: data.email,
            password: data.password,
            role: data.role,
            branch: data.branch,
            branchId: data.branchId,
            managerId: data.managerId,
            team: data.team,
            phone: data.phone,
            status: data.status,
          },
          { headers: authHeader() }
        );
        const created = res.data?.data;
        const newUser: User = {
          id: created?.id || String(Date.now()),
          name: created?.name || data.name,
          email: created?.email || data.email,
          phone: data.phone,
          role: data.role,
          branch: data.branch,
          branchId: data.branchId,
          managerId: data.managerId,
          team: data.team,
          status: data.status,
          isActive: data.status === "Active",
          companyId: created?.company_id || "",
          lastLogin: "Never",
          createdAt: created?.created_at || new Date().toISOString(),
        };
        setUsers((prev) => [newUser, ...prev]);
        showToast("User created successfully", "success");
        setModalOpen(false);
        return;
      }
      await fetchAllUsers();
      setModalOpen(false);
    } catch (err: any) {
      showToast(err.response?.data?.message || "Failed to save user", "error");
      // ✅ Modal band mat karo on error
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Delete ───────────────────────────────────────────────
  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setIsDeleting(true);
    try {
      await axios.delete(
        `${API}/auth/company-user/delete/${deleteConfirm}?permanent=true`,
        { headers: authHeader() }
      );
      showToast("User deleted successfully", "success");
      await fetchAllUsers();
    } catch (err: any) {
      showToast(err.response?.data?.message || "Failed to delete user", "error");
    } finally {
      setDeleteConfirm(null);
      setIsDeleting(false);
    }
  };

  // ─── Deactivate ───────────────────────────────────────────
  const getDeactStats = (userName: string) => {
    if (userName === "Rajesh Kumar") return { leads: 34, chats: 34, calls: 5, workflows: 2 };
    if (userName === "Priya Sharma") return { leads: 28, chats: 28, calls: 3, workflows: 1 };
    if (userName === "Amit Patel") return { leads: 12, chats: 12, calls: 1, workflows: 0 };
    if (userName === "Sneha Rao") return { leads: 18, chats: 18, calls: 2, workflows: 0 };
    return { leads: 0, chats: 0, calls: 0, workflows: 0 };
  };

  const handleDeactivateConfirm = async () => {
    if (!deactivateUser) return;
    try {
      await axios.delete(
        `${API}/auth/company-user/delete/${deactivateUser.id}`,
        { headers: authHeader() }
      );
      showToast(`${deactivateUser.name} deactivated successfully`, "success");
      await fetchAllUsers();
    } catch (err: any) {
      showToast(err.response?.data?.message || "Failed to deactivate user", "error");
    } finally {
      setDeactivateUser(null);
    }
  };

  // ─── Fetch positions ─────────────────────────────────────
  const fetchPositions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/positions`, { headers: authHeader() });
      setPositionsList(res.data?.data?.items || []);
    } catch (err: any) {
      console.error(err.response?.data?.message || "Failed to load positions");
    }
  }, []);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  // ─── Fetch branches ──────────────────────────────────────
  const fetchBranches = useCallback(async () => {
    try {
      const { items } = await branchesApi.list();
      setBranchList(items);
    } catch (err) {
      console.error(apiError(err, "Couldn't load branches"));
    }
  }, []);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  return (
    <CrmLayout>
      <div className="p-6 space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Users</h1>
            <p className="text-sm text-gray-500 mt-0.5">{users.length} total users</p>
          </div>
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line" />
            Add User
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />
          </div>
          <select value={positionFilter} onChange={(e) => setPositionFilter(e.target.value)}
            className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none cursor-pointer">
            <option value="all">All Positions</option>
            {positionsList.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
          </select>
          <select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value as Branch | "all")}
            className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none cursor-pointer">
            <option value="all">All Branches</option>
            {branchList.map((b) => <option key={b.name} value={b.name}>{b.name}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as Status | "all")}
            className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none cursor-pointer">
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="py-16 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="py-16 text-center">
              <i className="ri-user-search-line text-4xl text-gray-300 block mb-3" />
              <p className="text-gray-500 text-sm">No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {["User", "Role", "Branch", "Status", "Last Login", "Actions"].map((h, i) => (
                      <th key={h} className={`px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap ${i === 5 ? "text-right" : "text-left"}`}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => {
                    const pos = getUserPosition(u.role);
                    return (
                      <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold text-sm shrink-0">
                              {u.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 whitespace-nowrap">
                                {u.name}
                                {isSelf(u.id) && <span className="ml-1.5 text-[10px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded font-medium">You</span>}
                              </p>
                              <p className="text-xs text-gray-400">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${positionColors[u.role] || "bg-gray-100 text-gray-600"}`}>
                            {pos?.name || u.role}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{u.branch || "—"}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[u.status] || "bg-gray-100 text-gray-500"}`}>
                            {u.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{u.lastLogin}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => handleEdit(u)}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors cursor-pointer" title="Edit">
                              <i className="ri-edit-line text-sm" />
                            </button>
                            {!isSelf(u.id) && u.role !== "super_admin" && u.role !== "owner_/_ceo" && (
                              <>
                                <button onClick={() => setDeactivateUser(u)}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors cursor-pointer" title="Deactivate">
                                  <i className="ri-user-unfollow-line text-sm" />
                                </button>
                                <button onClick={() => handleDelete(u.id)}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer" title="Delete">
                                  <i className="ri-delete-bin-line text-sm" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* User Modal */}
        {modalOpen && (
          <UserModal
            user={editingUser}
            isSaving={isSaving}
            onClose={() => setModalOpen(false)}
            onSave={handleSave}
          />
        )}

        {/* Delete Confirm Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl w-full max-w-sm shadow-lg p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <i className="ri-delete-bin-line text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Delete User</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone.</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5 justify-end pt-2">
                <button onClick={() => setDeleteConfirm(null)} disabled={isDeleting}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50">
                  Cancel
                </button>
                <button onClick={confirmDelete} disabled={isDeleting}
                  className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-sm font-medium text-white transition-colors whitespace-nowrap disabled:opacity-50 flex items-center gap-2">
                  {isDeleting ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Deleting...</> : "Delete Permanently"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Deactivation Modal */}
        {deactivateUser && (() => {
          const stats = getDeactStats(deactivateUser.name);
          console.log("XXXXXXXXX==>", stats)
          const hasItems = stats.leads > 0 || stats.chats > 0 || stats.calls > 0 || stats.workflows > 0;
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
              <div className="bg-white rounded-xl w-full max-w-md shadow-lg max-h-[90vh] overflow-y-auto">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Deactivate {deactivateUser.name}?</h2>
                </div>
                <div className="px-6 py-5 space-y-4">
                  {hasItems && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <i className="ri-error-warning-line text-amber-600" />
                        <span className="text-sm font-semibold text-amber-800">Active items to reassign</span>
                      </div>
                      <div className="space-y-1.5 text-sm">
                        {stats.leads > 0 && <div className="flex justify-between text-amber-800"><span>Active leads</span><span className="font-semibold">{stats.leads}</span></div>}
                        {stats.chats > 0 && <div className="flex justify-between text-amber-800"><span>Open chats</span><span className="font-semibold">{stats.chats}</span></div>}
                        {stats.calls > 0 && <div className="flex justify-between text-amber-800"><span>Scheduled calls</span><span className="font-semibold">{stats.calls}</span></div>}
                        {stats.workflows > 0 && <div className="flex justify-between text-amber-800"><span>Active workflows</span><span className="font-semibold">{stats.workflows}</span></div>}
                      </div>
                    </div>
                  )}
                  {hasItems && (
                    <div className="space-y-4">
                      <p className="text-sm font-semibold text-gray-700">Reassign all to:</p>
                      {stats.leads > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-gray-600">Leads ({stats.leads})</p>
                          <select value={deactReassignLeadsTo} onChange={(e) => setDeactReassignLeadsTo(e.target.value)}
                            disabled={deactUseAssignmentRules}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm disabled:opacity-40">
                            <option value="">Select agent...</option>
                            {allAgents.filter((a) => a !== deactivateUser.name).map((a) => <option key={a} value={a}>{a}</option>)}
                          </select>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={deactUseAssignmentRules} onChange={(e) => setDeactUseAssignmentRules(e.target.checked)} className="w-3.5 h-3.5 accent-indigo-600" />
                            <span className="text-xs text-gray-600">Use assignment rules (auto-distribute)</span>
                          </label>
                        </div>
                      )}
                      {stats.chats > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-gray-600">Chats ({stats.chats})</p>
                          <select value={deactReassignChatsTo} onChange={(e) => setDeactReassignChatsTo(e.target.value)}
                            disabled={deactDistributeChatsEvenly}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm disabled:opacity-40">
                            <option value="">Select agent...</option>
                            {allAgents.filter((a) => a !== deactivateUser.name).map((a) => <option key={a} value={a}>{a}</option>)}
                          </select>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={deactDistributeChatsEvenly} onChange={(e) => setDeactDistributeChatsEvenly(e.target.checked)} className="w-3.5 h-3.5 accent-indigo-600" />
                            <span className="text-xs text-gray-600">Distribute evenly</span>
                          </label>
                        </div>
                      )}
                      {stats.calls > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-gray-600">Calls ({stats.calls})</p>
                          <select value={deactReassignCallsTo} onChange={(e) => setDeactReassignCallsTo(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm">
                            <option value="">Select agent...</option>
                            {allAgents.filter((a) => a !== deactivateUser.name).map((a) => <option key={a} value={a}>{a}</option>)}
                          </select>
                        </div>
                      )}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={deactNotifyTeam} onChange={(e) => setDeactNotifyTeam(e.target.checked)} className="w-3.5 h-3.5 accent-indigo-600" />
                        <span className="text-xs text-gray-600">Notify team about workload changes</span>
                      </label>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2.5 px-6 py-4 border-t border-gray-200 justify-end">
                  <button onClick={() => setDeactivateUser(null)}
                    className="px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 whitespace-nowrap">
                    Cancel
                  </button>
                  <button onClick={handleDeactivateConfirm}
                    className="px-4 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-sm font-medium text-white whitespace-nowrap">
                    Deactivate &amp; Reassign
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </CrmLayout>
  );
}